import { Helmet } from 'react-helmet-async';
// @mui
import {
    Card,
    Stack,
    Container,
    Typography,
    TableContainer,
    CardContent,
    Button,
    CircularProgress,
    Box,
    ButtonGroup,
    TableHead,
    TableRow,
    TableCell,
    Table,
    TableBody,
} from '@mui/material';

import {useNavigate, useParams} from 'react-router-dom';

// components
import Scrollbar from '../components/scrollbar';
// sections
import { StepByStepTable } from "../sections/@dashboard/attestations";
import {GeneralLayout} from "../layouts/GeneralLayout";
import Label from "../components/label";
import TextField from "@mui/material/TextField";
import {useEffect, useState} from "react";
import {useGetNextAttestationNumberQuery, usePostCreateAttestationMutation} from "../store/attestation/attestationApi";
import PageTitle from "../components/PageTitle";
import {useGetFacultiesQuery} from "../store/faculty/facultyApi";
import MenuItem from "@mui/material/MenuItem";
import copyObject from "../utils/copyObject";
import {PrintOutlined, RemoveRedEye} from "@mui/icons-material";
import {useLazyAcademicPerformanceByPeriodWebReportQuery} from "../store/reports/reportsApi";

export default function AcademicPerformanceByPeriodReportPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const {data:facultiesData, isLoading:facultiesAreLoading} = useGetFacultiesQuery();
    const [faculties, setFaculties] = useState([]);
    const [selectedFacultyId, setSelectedFacultyId] = useState(0);
    const [fromDate, setFromDate] = useState(formatDate(new Date()));
    const [toDate, setToDate] = useState(formatDate(new Date()));
    const [reportData] = useLazyAcademicPerformanceByPeriodWebReportQuery();
    const [copyOfData, setCopyOfData] = useState([]);
    
    useEffect(() => {
        if (facultiesData?.data === null || facultiesData?.data === undefined) {
            return;
        }
        
        setFaculties(copyObject(facultiesData?.data));
    }, [facultiesData])

    function formatDate(dateObj) {
        return `${dateObj.getFullYear()}-${withZero(dateObj.getMonth() + 1)}-${withZero(dateObj.getDate())}`;
    }

    function toDateString(date){
        if (date === null || date === undefined)
            return '';

        return new Date(date).toLocaleDateString("ru-RU");
    }

    function withZero(num) {
        return num < 10 ? `0${num}` : num;
    }

    function getPrintableReport() {
        setIsLoading(true);
        fetch(`/Reports/AcademicPerformanceByPeriodPrintableReport?from=${fromDate}&to=${toDate}&facultyId=${selectedFacultyId ?? 0}&fileType=xlsx`,
            {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem('token')}`,
                    "semesterid": localStorage.getItem("selectedSemesterId")
                }
            }).then(response => {
            setIsLoading(false);
            return response.blob();
        }).then(response => {
            const url = window.URL.createObjectURL(
                new Blob([response]),
            );
            const link = document.createElement('a');
            link.href = url;
            const date = new Date()
            link.setAttribute(
                'download',
                `Отчет_Успеваемость_${formatDate(date)}.xlsx`,
            );

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
        })
    }

    const loadData = async () => {
        setIsLoading(true);

        await reportData({data: {
                facultyId: parseInt(selectedFacultyId ?? 0, 10),
                from: fromDate,
                to: toDate
            }}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                setCopyOfData(copyObject(fulfilled.data));
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });
            }
            setIsLoading(false);
        }).catch((ex) => {
            alert('Ошибка');
            setIsLoading(false);
        });
    };
    
    function canLoadData() {
        return selectedFacultyId === null || selectedFacultyId === undefined 
            || fromDate === null || fromDate === undefined 
            || toDate === null || toDate === undefined;
    }

    return (
        <GeneralLayout>
            <PageTitle title={`Успеваемость за период`} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {facultiesAreLoading ? <CircularProgress /> : <></>}
                            Успеваемость за период
                        </Typography>
                    </Stack>

                    <Card>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                                    <Typography>Фильтры</Typography>
                                    <TextField label="С" 
                                               fullWidth 
                                               variant="outlined" 
                                               size={'small'} 
                                               type={'date'} 
                                               defaultValue={formatDate(new Date())}
                                               onChange={(event) => {setFromDate(event.target.value)}}/>
                                    <TextField label="По"
                                               fullWidth
                                               variant="outlined"
                                               size={'small'}
                                               type={'date'}
                                               defaultValue={formatDate(new Date())}
                                               onChange={(event) => {setFromDate(event.target.value)}}/>
                                    <TextField
                                        select
                                        fullWidth
                                        value={selectedFacultyId}
                                        onChange={(event) => {setSelectedFacultyId(event.target.value)}}
                                        label="Факультет"
                                        size={'small'}
                                    >
                                        <MenuItem key={0} value={''}>Все</MenuItem>
                                        {faculties?.sort((l,r) => l.value > r.value ? 1 : -1).map((item) => {
                                            return <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>
                                        })}
                                    </TextField>
                                </Stack>
                                
                                <ButtonGroup orientation={'vertical'}>
                                    <Button variant={'outlined'}
                                            disabled={canLoadData() || isLoading}
                                            onClick={() => {getPrintableReport()}}
                                            startIcon={isLoading ? <CircularProgress size={15} /> : <PrintOutlined />}>Печать XLSX</Button>
                                    <Button variant={'contained'}
                                            disabled={canLoadData() || isLoading}
                                            onClick={loadData}
                                            startIcon={isLoading ? <CircularProgress size={15} /> : <RemoveRedEye />}>Просмотр</Button>
                                </ButtonGroup>
                            </Stack>
                        </CardContent>
                    </Card>
                    
                    <Typography fontSize={'small'}>* в расчет также берутся оценки по практикам</Typography>
                    
                    <Card>
                        <TableContainer>
                            <Table size={'small'}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Факультет</TableCell>
                                        <TableCell>Образовательная программа</TableCell>
                                        <TableCell>Группа</TableCell>
                                        <TableCell>Уровень образования</TableCell>
                                        <TableCell>Курс</TableCell>
                                        <TableCell>ФИО</TableCell>
                                        <TableCell>Фин. форма</TableCell>
                                        <TableCell>Оценка 5</TableCell>
                                        <TableCell>Оценка 4</TableCell>
                                        <TableCell>Оценка 3</TableCell>
                                        <TableCell>Оценка 2</TableCell>
                                        <TableCell>Оценка "зачтено"</TableCell>
                                        <TableCell>Оценка "не зачтено"</TableCell>
                                        <TableCell>Неявка</TableCell>
                                        <TableCell>Не допущен</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {copyOfData?.map((item) => {
                                        return <TableRow>
                                            <TableCell>{item.facultyName}</TableCell>
                                            <TableCell>{item.courseName}</TableCell>
                                            <TableCell>{item.groupName}</TableCell>
                                            <TableCell>{item.educationLevel}</TableCell>
                                            <TableCell>{item.courseNumber}</TableCell>
                                            <TableCell>{item.fullName}</TableCell>
                                            <TableCell>{item.financeFormName}</TableCell>
                                            <TableCell>{item.grades5Quantity}</TableCell>
                                            <TableCell>{item.grades4Quantity}</TableCell>
                                            <TableCell>{item.grades3Quantity}</TableCell>
                                            <TableCell>{item.grades2Quantity}</TableCell>
                                            <TableCell>{item.gradesCheckPassedQuantity}</TableCell>
                                            <TableCell>{item.gradesCheckNotPassedQuantity}</TableCell>
                                            <TableCell>{item.gradesNotAppearedQuantity}</TableCell>
                                            <TableCell>{item.gradesNotAllowedQuantity}</TableCell>
                                        </TableRow>
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Card>
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
